import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  Maximize2, 
  ArrowLeft, 
  ArrowRight,
  Phone, 
  Mail, 
  Calendar,
  CheckCircle,
  Home,
  TreePine,
  Car,
  Droplets,
  Zap,
  ChevronRight,
  Share2,
  Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { terrainListings } from '../mockData';
import AnimatedSection from '../components/AnimatedSection';
import MapView from '../components/MapView';

const TerrainDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [terrain, setTerrain] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isGalleryHovered, setIsGalleryHovered] = useState(false);

  useEffect(() => {
    const foundTerrain = terrainListings.find(t => t.id === parseInt(id));
    if (foundTerrain) {
      setTerrain(foundTerrain);
    }
  }, [id]);

  useEffect(() => {
    if (
      !terrain ||
      !terrain.gallery ||
      !Array.isArray(terrain.gallery) ||
      terrain.gallery.length <= 1 ||
      isGalleryHovered
    ) {
      return;
    }

    const interval = setInterval(() => {
      setActiveImageIndex((prevIndex) => {
        const total = terrain.gallery.length;
        return total > 0 ? (prevIndex + 1) % total : 0;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [terrain, isGalleryHovered]);

  if (!terrain) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Terrain introuvable</h1>
          <Button onClick={() => navigate('/nos-terrains')} className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800">
            Retour aux terrains
          </Button>
        </div>
      </div>
    );
  }

  // Генерируем расширенные данные для terrain
  const getTerrainDetails = (terrain) => {
    // Если у terrain есть расширенные данные, используем их
    if (terrain.gallery && terrain.gallery.length > 0) {
      return {
        description: terrain.description || terrain.longDescription || `Magnifique terrain constructible situé à ${terrain.location || terrain.fullAddress || terrain.commune}.`,
        longDescription: terrain.longDescription || terrain.description,
        features: terrain.features ? [
          { icon: Home, label: 'Constructible', value: terrain.features.constructible ? 'Oui' : 'Non' },
          { icon: TreePine, label: 'Orientation', value: terrain.features.orientation || 'Variable' },
          { icon: Car, label: 'Accès', value: terrain.features.access || 'Route goudronnée' },
          { icon: Droplets, label: 'Viabilisation', value: terrain.features.viabilisation || 'À prévoir' },
          { icon: Zap, label: 'Électricité', value: terrain.features.electricity || 'Disponible' },
          { icon: Maximize2, label: 'Forme', value: terrain.features.shape || 'À définir' }
        ] : [
          { icon: Home, label: 'Constructible', value: 'Oui' },
          { icon: TreePine, label: 'Orientation', value: 'Variable' },
          { icon: Car, label: 'Accès', value: 'Route goudronnée' },
          { icon: Droplets, label: 'Viabilisation', value: 'À prévoir' },
          { icon: Zap, label: 'Électricité', value: 'Disponible' },
          { icon: Maximize2, label: 'Forme', value: 'À définir' }
        ],
        characteristics: {
          surface: terrain.surface || 'À définir',
          pricePerM2: terrain.surface && terrain.price > 0 ? Math.round(terrain.price / terrain.surface) : null,
          orientation: terrain.features?.orientation || 'Variable',
          altitude: terrain.altitude ? `${terrain.altitude} m` : null,
          slope: 'Variable',
          soil: 'Terre végétale',
          zoning: 'Zone constructible',
          buildingRight: terrain.features?.constructible ? 'Oui' : 'À vérifier',
          maxHeight: 'Selon PLU',
          distanceToCenter: terrain.nearbyPoints?.[0]?.distance || 'Variable'
        },
        gallery: terrain.gallery,
        nearbyFacilities: terrain.nearbyPoints ? terrain.nearbyPoints.map(p => `${p.name} (${p.distance})`) : [
          'Écoles primaires et secondaires',
          'Commerces de proximité',
          'Transport en commun',
          'Services médicaux',
          'Espaces verts'
        ],
        nearbyPoints: terrain.nearbyPoints || []
      };
    }

    // Для обычных terrains используем стандартную логику
    const hasConstruction = terrain.title.toLowerCase().includes('projet de construction');
    const isViabilise = terrain.title.toLowerCase().includes('viabilisé');
    
    return {
      description: `Magnifique terrain constructible situé à ${terrain.location}, idéal pour votre projet de construction. Ce terrain de ${terrain.surface} m² offre un emplacement privilégié dans une zone résidentielle recherchée.${hasConstruction ? ' Un projet de construction est déjà inclus avec ce terrain, vous permettant de démarrer rapidement votre projet de vie.' : ''}${isViabilise ? ' Le terrain est déjà viabilisé, ce qui simplifie grandement votre projet.' : ''}`,
      features: [
        { icon: Home, label: 'Constructible', value: 'Oui' },
        { icon: TreePine, label: 'Orientation', value: 'Sud/Sud-Ouest' },
        { icon: Car, label: 'Accès', value: 'Route goudronnée' },
        { icon: Droplets, label: 'Viabilisation', value: isViabilise ? 'Complète' : 'À prévoir' },
        { icon: Zap, label: 'Électricité', value: 'Disponible' },
        { icon: Maximize2, label: 'Forme', value: 'Rectangulaire' }
      ],
      characteristics: {
        surface: terrain.surface,
        pricePerM2: terrain.surface > 0 ? Math.round(terrain.price / terrain.surface) : null,
        orientation: 'Sud/Sud-Ouest',
        slope: 'Faible (< 5%)',
        soil: 'Terre végétale',
        zoning: 'Zone résidentielle',
        buildingRight: 'Oui',
        maxHeight: '8 mètres',
        distanceToCenter: '2-5 km'
      },
      gallery: [
        terrain.image,
        'https://images.unsplash.com/photo-1587745890135-20db8c79b027?w=800',
        'https://images.unsplash.com/photo-1465541064977-5a2d76b09f1f?w=800',
        'https://images.unsplash.com/photo-1506695041619-5dd4f46960b7?w=800'
      ],
      nearbyFacilities: [
        'Écoles primaires et secondaires',
        'Commerces de proximité',
        'Transport en commun',
        'Services médicaux',
        'Espaces verts'
      ],
      nearbyPoints: []
    };
  };

  const details = getTerrainDetails(terrain);
  
  // Находим похожие terrains
  const similarTerrains = terrainListings
    .filter(t => t.id !== terrain.id && t.location === terrain.location)
    .slice(0, 3);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: terrain.title,
          text: `Découvrez ce terrain à ${terrain.location}`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papiers !');
    }
  };

  const handlePrevImage = () => {
    if (!details.gallery || details.gallery.length === 0) return;
    setActiveImageIndex((prevIndex) => {
      const total = details.gallery.length;
      if (total === 0) return 0;
      return (prevIndex - 1 + total) % total;
    });
  };

  const handleNextImage = () => {
    if (!details.gallery || details.gallery.length === 0) return;
    setActiveImageIndex((prevIndex) => {
      const total = details.gallery.length;
      if (total === 0) return 0;
      return (prevIndex + 1) % total;
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <AnimatedSection animation="fade-in">
        <div className="bg-gray-50 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center text-sm text-gray-600">
              <Link to="/" className="hover:text-[#c4d600] transition-colors">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <Link to="/nos-terrains" className="hover:text-[#c4d600] transition-colors">Nos terrains</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-gray-800 font-medium">{terrain.title}</span>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Hero Section with Image Gallery */}
      <AnimatedSection animation="fade-up">
        <div className="relative">
          <div
            className="relative h-[60vh] md:h-[70vh] overflow-hidden bg-gray-200"
            onMouseEnter={() => setIsGalleryHovered(true)}
            onMouseLeave={() => setIsGalleryHovered(false)}
          >
            <img
              src={details.gallery[activeImageIndex]}
              alt={terrain.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/1200x800?text=Image+non+disponible';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            
            {/* Arrow Navigation */}
            {details.gallery.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all z-10"
                  aria-label="Image précédente"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all z-10"
                  aria-label="Image suivante"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </>
            )}
            
            {/* Image Navigation */}
            {details.gallery.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                {details.gallery.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImageIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      activeImageIndex === index ? 'bg-[#c4d600] w-8' : 'bg-white/50 hover:bg-white/80'
                    }`}
                    aria-label={`Voir l'image ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="absolute top-4 right-4 flex space-x-2 z-10">
              <button
                onClick={() => setIsFavorite(!isFavorite)}
                className={`p-3 rounded-full backdrop-blur-md transition-all ${
                  isFavorite 
                    ? 'bg-[#c4d600] text-gray-800' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                aria-label={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
              <button
                onClick={handleShare}
                className="p-3 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all"
                aria-label="Partager"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>

            {/* Back Button */}
            <div className="absolute top-4 left-4 z-10">
              <Button
                onClick={() => navigate('/nos-terrains')}
                variant="ghost"
                className="bg-white/20 backdrop-blur-md text-white hover:bg-white/30"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </div>

            {/* Title Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <div className="max-w-7xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">{terrain.title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-lg">
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-[#c4d600]" />
                    <span>{terrain.fullAddress || terrain.location}</span>
                  </div>
                  {terrain.surface > 0 && (
                    <div className="flex items-center">
                      <Maximize2 className="w-5 h-5 mr-2 text-[#c4d600]" />
                      <span>{terrain.surface} m²</span>
                    </div>
                  )}
                  {terrain.altitude && (
                    <div className="flex items-center">
                      <TreePine className="w-5 h-5 mr-2 text-[#c4d600]" />
                      <span>{terrain.altitude} m d'altitude</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Price and Quick Info */}
      <AnimatedSection animation="fade-up" delay={0.1}>
        <div className="bg-[#c4d600] py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div>
                <p className="text-gray-800 text-sm font-semibold mb-1">Prix</p>
                {terrain.price > 0 ? (
                  <>
                    <p className="text-4xl font-bold text-gray-800">{terrain.price.toLocaleString('fr-FR')} €</p>
                    {details.characteristics.pricePerM2 && (
                      <p className="text-gray-700 text-sm mt-1">
                        {details.characteristics.pricePerM2.toLocaleString('fr-FR')} € / m²
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-4xl font-bold text-gray-800">Sur demande</p>
                )}
              </div>
              <div className="flex flex-wrap gap-4">
                {terrain.surface > 0 && (
                  <div className="text-center">
                    <p className="text-gray-800 text-sm font-semibold">Surface</p>
                    <p className="text-2xl font-bold text-gray-800">{terrain.surface} m²</p>
                  </div>
                )}
                <div className="text-center">
                  <p className="text-gray-800 text-sm font-semibold">Localisation</p>
                  <p className="text-lg font-bold text-gray-800">{terrain.commune || terrain.location}</p>
                  {terrain.department && (
                    <p className="text-sm text-gray-700">{terrain.department}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  asChild
                  className="bg-gray-800 hover:bg-gray-900 text-white"
                >
                  <a href={`tel:0492000000`}>
                    <Phone className="w-4 h-4 mr-2" />
                    Appeler
                  </a>
                </Button>
                <Button
                  asChild
                  className="bg-gray-800 hover:bg-gray-900 text-white"
                >
                  <a href={`mailto:Contact@artduchalet.com?subject=Demande d'information - ${terrain.title}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    Contacter
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <AnimatedSection animation="fade-up">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Description</h2>
                {details.longDescription ? (
                  <div className="text-gray-600 text-lg leading-relaxed whitespace-pre-line">
                    {details.longDescription.split('\n\n').map((paragraph, index) => (
                      paragraph.trim() && (
                        <p key={index} className={paragraph.startsWith('**') ? 'font-bold text-gray-800 mt-4 mb-2' : 'mb-4'}>
                          {paragraph.replace(/\*\*/g, '')}
                        </p>
                      )
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 text-lg leading-relaxed">{details.description}</p>
                )}
              </div>
            </AnimatedSection>

            {/* Features Grid */}
            <AnimatedSection animation="fade-up" delay={0.1}>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-6">Caractéristiques</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {details.features.map((feature, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-lg p-4 hover:bg-[#c4d600]/10 transition-colors group"
                    >
                      <feature.icon className="w-6 h-6 text-[#c4d600] mb-2 group-hover:scale-110 transition-transform" />
                      <p className="text-sm text-gray-600 mb-1">{feature.label}</p>
                      <p className="font-semibold text-gray-800">{feature.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            {/* Detailed Characteristics */}
            <AnimatedSection animation="fade-up" delay={0.2}>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-6">Détails techniques</h2>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Surface totale</span>
                      <span className="font-semibold text-gray-800">
                        {typeof details.characteristics.surface === 'number' ? `${details.characteristics.surface} m²` : details.characteristics.surface}
                      </span>
                    </div>
                    {details.characteristics.altitude && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Altitude</span>
                        <span className="font-semibold text-gray-800">{details.characteristics.altitude}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Orientation</span>
                      <span className="font-semibold text-gray-800">{details.characteristics.orientation}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Pente</span>
                      <span className="font-semibold text-gray-800">{details.characteristics.slope}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Nature du sol</span>
                      <span className="font-semibold text-gray-800">{details.characteristics.soil}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Zone d'urbanisme</span>
                      <span className="font-semibold text-gray-800">{details.characteristics.zoning}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Droit de construire</span>
                      <span className="font-semibold text-gray-800">{details.characteristics.buildingRight}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Hauteur max. autorisée</span>
                      <span className="font-semibold text-gray-800">{details.characteristics.maxHeight}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Distance centre-ville</span>
                      <span className="font-semibold text-gray-800">{details.characteristics.distanceToCenter}</span>
                    </div>
                    {details.characteristics.pricePerM2 && (
                      <div className="flex justify-between py-2 border-t border-gray-200 mt-2 pt-2">
                        <span className="text-gray-600">Prix au m²</span>
                        <span className="font-semibold text-gray-800">
                          {details.characteristics.pricePerM2.toLocaleString('fr-FR')} €
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Nearby Facilities */}
            <AnimatedSection animation="fade-up" delay={0.3}>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-6">À proximité</h2>
                <div className="bg-gradient-to-r from-[#c4d600]/10 to-transparent rounded-lg p-6">
                  {details.nearbyPoints && details.nearbyPoints.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {details.nearbyPoints.map((point, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <CheckCircle className="w-5 h-5 text-[#c4d600] mr-3 flex-shrink-0" />
                          <div>
                            <span className="font-semibold">{point.name}</span>
                            <span className="text-gray-600 ml-2">({point.distance})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      {details.nearbyFacilities.map((facility, index) => (
                        <li key={index} className="flex items-center text-gray-700">
                          <CheckCircle className="w-5 h-5 text-[#c4d600] mr-3 flex-shrink-0" />
                          <span>{facility}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </AnimatedSection>

            {/* Map */}
            <AnimatedSection animation="fade-up" delay={0.4}>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-6">Localisation</h2>
                <div className="rounded-lg overflow-hidden shadow-lg h-96">
                  <MapView terrains={[terrain]} />
                </div>
                <div className="mt-4 flex items-center text-gray-600">
                  <MapPin className="w-5 h-5 mr-2 text-[#c4d600]" />
                  <span>
                    {terrain.fullAddress || terrain.location}
                    {terrain.coordinates && ` - Coordonnées: ${terrain.coordinates.lat}° N, ${terrain.coordinates.lng}° E`}
                  </span>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <AnimatedSection animation="slide-left">
              <div className="bg-gray-50 rounded-lg p-6 sticky top-24">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Intéressé par ce terrain ?</h3>
                <p className="text-gray-600 mb-6">
                  Contactez-nous pour plus d'informations ou pour organiser une visite.
                </p>
                <div className="space-y-3">
                  <Button
                    asChild
                    className="w-full bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold"
                  >
                    <a href={`mailto:Contact@artduchalet.com?subject=Demande d'information - ${terrain.title}`}>
                      <Mail className="w-4 h-4 mr-2" />
                      Envoyer un message
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full"
                  >
                    <a href={`tel:0492000000`}>
                      <Phone className="w-4 h-4 mr-2" />
                      Appeler maintenant
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full"
                  >
                    <Link to="/contact">
                      <Calendar className="w-4 h-4 mr-2" />
                      Prendre rendez-vous
                    </Link>
                  </Button>
                </div>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Référence terrain</p>
                  <p className="font-mono text-lg font-bold text-gray-800">#{terrain.id.toString().padStart(4, '0')}</p>
                </div>
              </div>
            </AnimatedSection>

            {/* Quick Stats */}
            <AnimatedSection animation="slide-left" delay={0.1}>
              <div className="bg-gradient-to-br from-[#c4d600] to-[#b0c200] rounded-lg p-6 text-white">
                <h3 className="text-xl font-bold mb-4">Résumé</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="opacity-90">Prix</span>
                    <span className="font-bold">
                      {terrain.price > 0 ? `${terrain.price.toLocaleString('fr-FR')} €` : 'Sur demande'}
                    </span>
                  </div>
                  {terrain.surface > 0 && (
                    <div className="flex justify-between">
                      <span className="opacity-90">Surface</span>
                      <span className="font-bold">{terrain.surface} m²</span>
                    </div>
                  )}
                  {details.characteristics.pricePerM2 && (
                    <div className="flex justify-between">
                      <span className="opacity-90">Prix au m²</span>
                      <span className="font-bold">{details.characteristics.pricePerM2.toLocaleString('fr-FR')} €</span>
                    </div>
                  )}
                  {terrain.altitude && (
                    <div className="flex justify-between">
                      <span className="opacity-90">Altitude</span>
                      <span className="font-bold">{terrain.altitude} m</span>
                    </div>
                  )}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* Similar Terrains */}
      {similarTerrains.length > 0 && (
        <AnimatedSection animation="fade-up">
          <div className="bg-gray-50 py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-8">Terrains similaires à {terrain.location}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {similarTerrains.map((similarTerrain) => (
                  <Link
                    key={similarTerrain.id}
                    to={`/terrain/${similarTerrain.id}`}
                    className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={similarTerrain.image}
                        alt={similarTerrain.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/800x600?text=Image+non+disponible';
                        }}
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-gray-800 mb-2 group-hover:text-[#c4d600] transition-colors">
                        {similarTerrain.title}
                      </h3>
                      <p className="text-2xl font-bold text-[#c4d600] mb-2">
                        {similarTerrain.price.toLocaleString('fr-FR')} €
                      </p>
                      <div className="flex items-center text-gray-600 text-sm">
                        <Maximize2 className="w-4 h-4 mr-1 text-[#c4d600]" />
                        <span>{similarTerrain.surface} m²</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </AnimatedSection>
      )}

      {/* CTA Section */}
      <AnimatedSection animation="fade-up">
        <div className="bg-[#c4d600] py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Prêt à construire votre projet ?
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Contactez notre équipe pour discuter de votre projet de construction sur ce terrain.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-gray-800 hover:bg-gray-900 text-white"
              >
                <Link to="/contact">
                  Demander un devis
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white"
              >
                <Link to="/nos-terrains">
                  Voir tous les terrains
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default TerrainDetailPage;

